import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../components/LegalPage';
import { SITE } from '../content/site';
import MailLink from '../components/MailLink';

const sections = [
  {
    id: 'summary',
    title: 'The short version',
    content: (
      <>
        <div className="callout">
          <p>
            Most of our apps work without an account, and what they store stays
            on your phone. The data that does leave your device is what Google
            needs to show ads, to tell us an app crashed, and — only in apps
            that offer sign-in — the account you chose to create. We do not sell
            personal data, and we do not buy it.
          </p>
        </div>
        <p>
          This policy covers every mobile application published by {SITE.name}{' '}
          on Google Play under developer ID {SITE.playDeveloperId}, any build of
          those apps distributed elsewhere, and this website. Where a specific
          app does something different, its store listing says so.
        </p>
      </>
    ),
  },
  {
    id: 'who-we-are',
    title: 'Who we are',
    content: (
      <p>
        {SITE.name} is an independent mobile app studio. For anything on this
        page — access, correction, deletion or a complaint — write to{' '}
        <MailLink subject="Privacy request" address />. We are the data
        controller for the information described below.
      </p>
    ),
  },
  {
    id: 'what-we-collect',
    title: 'What we collect, and why',
    content: (
      <>
        <p>
          Nothing here is collected for its own sake. Each row exists because a
          feature stops working without it.
        </p>
        <table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Why</th>
              <th>Where it lives</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Your content inside the app — notes, saved items, history, settings, game progress</td>
              <td>It is the app&apos;s purpose</td>
              <td>On your device. Removed when you clear the app&apos;s data or uninstall it</td>
            </tr>
            <tr>
              <td>Account details (name, email address, profile picture) — only in apps that offer sign-in</td>
              <td>To create your account and sync it to a new phone</td>
              <td>Google Firebase, until you delete the account</td>
            </tr>
            <tr>
              <td>Advertising ID and coarse ad-serving signals</td>
              <td>Google AdMob shows the ads that keep the free tier free</td>
              <td>Handled by Google; resettable in your device settings</td>
            </tr>
            <tr>
              <td>Crash reports and basic diagnostics: device model, OS version, app version, the state at the moment of a crash</td>
              <td>To find and fix the crash</td>
              <td>Google Play Console and Firebase, retained on their schedule</td>
            </tr>
            <tr>
              <td>Aggregate usage counts — screens opened, features used</td>
              <td>To see which features earn their place</td>
              <td>Firebase Analytics, in aggregate, not tied to your name</td>
            </tr>
            <tr>
              <td>Purchase and subscription status</td>
              <td>To unlock what you paid for and to turn ads off</td>
              <td>Google Play billing. We never see your card details</td>
            </tr>
            <tr>
              <td>Email you send us</td>
              <td>To answer you</td>
              <td>Our mailbox, kept while the matter is open</td>
            </tr>
          </tbody>
        </table>
        <p>
          We do not collect contacts, call logs, SMS, precise location or
          health data, and no app of ours records audio or video in the
          background.
        </p>
      </>
    ),
  },
  {
    id: 'permissions',
    title: 'Permissions, and what triggers them',
    content: (
      <>
        <p>
          Android asks before any of these. Declining one disables that feature
          and nothing else.
        </p>
        <ul>
          <li>
            <strong>Camera</strong> — only in apps whose job is to scan or
            photograph something. The frame is processed for that feature and is
            not uploaded unless the feature is explicitly a cloud one.
          </li>
          <li>
            <strong>Photos and files</strong> — to open an image you pick, or to
            save a file you asked the app to download.
          </li>
          <li>
            <strong>Notifications</strong> — for alarms, reminders and download
            completions. Never for marketing you did not ask for.
          </li>
          <li>
            <strong>Approximate location</strong> — only where a feature is
            location-dependent, and only while that feature is on screen.
          </li>
          <li>
            <strong>Internet and network state</strong> — to load content and to
            tell you when you are offline.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'ads',
    title: 'Advertising',
    content: (
      <>
        <p>
          Free apps carry ads from Google AdMob. Google may use your
          advertising ID and device signals to choose which ad to show and to
          count whether it was seen. Google&apos;s own policy governs that
          processing:{' '}
          <a
            href="https://policies.google.com/technologies/partner-sites"
            target="_blank"
            rel="noreferrer"
          >
            how Google uses data from apps that use its services
          </a>
          .
        </p>
        <p>
          In the EEA, the UK and Switzerland we show Google&apos;s consent form
          before any personalised ad request, and your answer is respected for
          as long as it stands — you can reopen that form from the app&apos;s
          settings at any time. Elsewhere, you can limit ad personalisation or
          reset your advertising ID from your device&apos;s own settings.
        </p>
        <p>
          Ads are never placed over the content you came for, and nothing about
          an ad is tied to a name or email address on our side.
        </p>
      </>
    ),
  },
  {
    id: 'processors',
    title: 'Who processes data for us',
    content: (
      <>
        <p>
          We use a small number of providers, each for a stated job, each bound
          by their own agreement with us:
        </p>
        <ul>
          <li>
            <strong>Google AdMob</strong> — serving and measuring ads in free
            apps.
          </li>
          <li>
            <strong>Google Firebase</strong> — accounts and sync where an app
            offers sign-in, crash reporting, and aggregate analytics.
          </li>
          <li>
            <strong>Google Play</strong> — distribution, billing, subscription
            state and crash reporting.
          </li>
        </ul>
        <p>
          These providers operate globally, so data may be processed outside
          your country, including in the United States, under the safeguards
          those providers publish. We do not sell personal data, and we do not
          share it with data brokers.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep things',
    content: (
      <ul>
        <li>
          Data on your device lives until you clear the app&apos;s storage or
          uninstall it.
        </li>
        <li>
          Account data lives until you delete the account — see{' '}
          <Link to="/delete-account">deleting your account</Link>.
        </li>
        <li>
          Crash and analytics records follow Google&apos;s retention windows,
          typically a few months, and are aggregated after that.
        </li>
        <li>
          Email correspondence is kept while the matter is open and for a short
          period afterwards.
        </li>
      </ul>
    ),
  },
  {
    id: 'your-rights',
    title: 'Your rights',
    content: (
      <>
        <p>
          Wherever you live, you can ask us what we hold about you, ask for it
          to be corrected, ask for it to be deleted, or object to a particular
          use. If you are in the EEA or the UK, the GDPR gives you those rights
          plus the right to portability and to complain to your national
          supervisory authority. If you are in India, the DPDP Act gives you
          equivalent rights and we act as the data fiduciary.
        </p>
        <p>
          Our legal bases are: performing the service you asked for, your
          consent (ads personalisation, optional permissions), and our
          legitimate interest in keeping the apps working and free of crashes.
        </p>
        <p>
          To exercise any of this, write to{' '}
          <MailLink subject="Privacy request" address />. We never charge for a
          request and we do not make you call anyone.
        </p>
      </>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    content: (
      <p>
        Our apps are not directed at children under 13, and we do not knowingly
        collect personal information from them. If you believe a child has
        provided us with personal data, write to{' '}
        <MailLink subject="Children's data" address /> and we will delete it.
      </p>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    content: (
      <p>
        Traffic between our apps and any server runs over HTTPS. Accounts, where
        they exist, are held in Google Firebase with access restricted to the
        studio. No system is perfect; if a breach ever affects your data we will
        say so, to you and to the relevant authority, within the deadlines the
        law sets.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    content: (
      <p>
        When this policy changes, the date at the top changes with it. Material
        changes are announced in the app or on its store listing before they
        take effect. Continuing to use an app after that date means the updated
        policy applies.
      </p>
    ),
  },
];

function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      applies={`Applies to all ${SITE.name} apps and ${SITE.domain}`}
      lede="What our apps collect, why each item exists, who processes it, and how to make it all go away."
      sections={sections}
      showAddress
    />
  );
}

export default Privacy;
